import type { TemplateQuery, TemplateVariable } from '@/services/api/dataApi';
import type {
  TemplateQuery as PersistedTemplateQuery,
  VariableDefinition,
} from '@/services/api/queryTemplateApi';
import type {
  AggregationFunction,
  ChartQueryDefinition,
  CompositeQueryDefinition,
  DataDiscoveryResult,
  TimeGranularity,
} from '@/types/chartDataSource';
import type { CreateTemplateResult } from './DataDiscoveryModal.createLogic';
import type { CompositeBindingSelection } from './compositeScope';

const TEMPLATE_PLACEHOLDER_PATTERN = /{{\s*[^}]+\s*}}/;
const UNSUPPORTED_AGGREGATION_REASON =
  'This generated template uses an unsupported aggregation and cannot be saved or applied directly.';
const MISSING_SINGLE_SOURCE_QUERY_REASON =
  'This generated result does not include a reusable single-source query.';
const UNRESOLVED_TEMPLATE_VARIABLE_REASON =
  'This generated template still contains unresolved template variables and cannot be applied directly.';
const UNSUPPORTED_GROUP_BY_GRANULARITY_REASON =
  'This generated template uses an unsupported group-by granularity and cannot be applied directly.';
const DIRECT_APPLY_FALLBACK_REASON =
  'This generated fallback result can be saved after review, but it cannot be applied directly.';
const COMPOSITE_SOURCE_ORG_REQUIRED_REASON =
  'This generated multi-source template is missing explicit source organization identity and cannot be saved or applied directly.';
export const PERSONAL_SPACE_SINGLE_SOURCE_SCOPE_ERROR =
  'Apply requires a resolved source organization in personal space. Save the template first or choose a specific source.';

const DIRECT_APPLY_BLOCKED_INTENTS = new Set(['multi_source_combined', 'period_comparison', 'derived_metric']);

export interface GeneratedCreateActionOptions {
  reviewAccepted?: boolean;
  isPersonalSpace?: boolean;
  discoveryResult?: DataDiscoveryResult | null;
  sourceOrganizationId?: string;
  sourceOrganizationName?: string;
}

export interface GeneratedCreateDisabledReasons {
  save: string | null;
  apply: string | null;
}

export interface GeneratedCreateActionState extends GeneratedCreateDisabledReasons {
  isActionable: boolean;
  canSave: boolean;
  canApply: boolean;
  requiresReviewAcceptance: boolean;
}

export interface GeneratedCreateSingleSourceQueryInput {
  templateQuery: TemplateQuery | undefined;
  templateVariables?: Record<string, TemplateVariable>;
  schemaName?: string;
  organizationId?: string;
  organizationName?: string;
}

export type GeneratedCreateApplyPayload =
  | {
      type: 'single-source';
      queryDefinition: ChartQueryDefinition;
      organizationId?: string;
      organizationName?: string;
    }
  | {
      type: 'composite';
      selection: CompositeBindingSelection;
    };

export type GeneratedCreateSingleSourceQueryResult =
  | {
      ok: true;
      queryDefinition: ChartQueryDefinition;
    }
  | {
      ok: false;
      disabledReason: string;
    };

export type GeneratedCreateApplyPayloadResult =
  | {
      ok: true;
      payload: GeneratedCreateApplyPayload;
    }
  | {
      ok: false;
      disabledReason: string;
    };

export type GeneratedCreateSavePayload =
  | {
      templateType: 'composite-query';
      compositeQueryDefinition: CompositeQueryDefinition;
    }
  | {
      query: PersistedTemplateQuery;
      variables: Record<string, VariableDefinition>;
    };

export type GeneratedCreateSavePayloadResult =
  | {
      ok: true;
      payload: GeneratedCreateSavePayload;
    }
  | {
      ok: false;
      disabledReason: string;
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeOptionalString(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

function normalizeUnknownString(value: unknown): string | undefined {
  return typeof value === 'string' ? normalizeOptionalString(value) : undefined;
}

function hasTemplatePlaceholder(value: string): boolean {
  return TEMPLATE_PLACEHOLDER_PATTERN.test(value);
}

function isConcreteTemplateString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && !hasTemplatePlaceholder(value);
}

function isSupportedAggregation(value: string): value is Exclude<AggregationFunction, 'none'> {
  switch (value) {
    case 'sum':
    case 'avg':
    case 'min':
    case 'max':
    case 'count':
    case 'last':
    case 'first':
      return true;
    default:
      return false;
  }
}

function isPersistedAggregation(value: string): value is PersistedTemplateQuery['measures'][number]['aggregation'] {
  switch (value) {
    case 'sum':
    case 'avg':
    case 'min':
    case 'max':
    case 'count':
    case 'last':
    case 'first':
      return true;
    default:
      return false;
  }
}

function isTimeGranularity(value: string): value is TimeGranularity {
  switch (value) {
    case 'minute':
    case 'hour':
    case 'day':
    case 'week':
    case 'month':
      return true;
    default:
      return false;
  }
}

function hasUnresolvedTemplatePlaceholder(value: unknown): boolean {
  if (typeof value === 'string') {
    return hasTemplatePlaceholder(value);
  }

  if (Array.isArray(value)) {
    return value.some((entry) => hasUnresolvedTemplatePlaceholder(entry));
  }

  if (isRecord(value)) {
    return Object.values(value).some((entry) => hasUnresolvedTemplatePlaceholder(entry));
  }

  return false;
}

function hasPotentialActionTarget(result: CreateTemplateResult): boolean {
  return result.kind === 'composite' || result.response.templateQuery !== undefined;
}

function getCompositeSourceIdentityDisabledReason(result: CreateTemplateResult): string | null {
  if (result.kind !== 'composite') {
    return null;
  }

  return result.definition.sources.every((source) => normalizeOptionalString(source.dataOwnerOrganizationId))
    ? null
    : COMPOSITE_SOURCE_ORG_REQUIRED_REASON;
}

interface GeneratedSourceOrganizationScope {
  organizationId: string;
  organizationName?: string;
}

function createOrganizationScope(
  organizationId: unknown,
  organizationName?: unknown,
): GeneratedSourceOrganizationScope | null {
  const normalizedOrganizationId = normalizeUnknownString(organizationId);
  if (!normalizedOrganizationId) {
    return null;
  }

  const normalizedOrganizationName = normalizeUnknownString(organizationName);
  return normalizedOrganizationName
    ? { organizationId: normalizedOrganizationId, organizationName: normalizedOrganizationName }
    : { organizationId: normalizedOrganizationId };
}

function getOrganizationScopeFromRecord(value: unknown): GeneratedSourceOrganizationScope | null {
  if (!isRecord(value)) {
    return null;
  }

  return createOrganizationScope(
    value.organizationId ?? value.orgId,
    value.organizationName ?? value.orgName,
  );
}

function getUniqueOrganizationScope(
  scopes: Array<GeneratedSourceOrganizationScope | null>,
): GeneratedSourceOrganizationScope | null {
  const scopeByOrganizationId = new Map<string, GeneratedSourceOrganizationScope>();

  for (const scope of scopes) {
    if (!scope) {
      continue;
    }

    const existingScope = scopeByOrganizationId.get(scope.organizationId);
    if (!existingScope || (!existingScope.organizationName && scope.organizationName)) {
      scopeByOrganizationId.set(scope.organizationId, scope);
    }
  }

  if (scopeByOrganizationId.size !== 1) {
    return null;
  }

  return Array.from(scopeByOrganizationId.values())[0] ?? null;
}

function sourceLabelMatches(value: string | undefined, sourceId: string | undefined, sourceName: string | undefined): boolean {
  if (!value) {
    return false;
  }

  return value === sourceId || value === sourceName;
}

function resolveDiscoveryOrganizationScope(
  result: Extract<CreateTemplateResult, { kind: 'template' | 'clarification' }>,
  discoveryResult: DataDiscoveryResult | null | undefined,
): GeneratedSourceOrganizationScope | null {
  const templateQuery = result.response.templateQuery;
  if (!templateQuery || !discoveryResult) {
    return null;
  }

  const schemaId = normalizeOptionalString(templateQuery.schemaId);
  const groupId = normalizeOptionalString(templateQuery.groupId);
  const sourceId = normalizeOptionalString(result.response.source?.id);
  const sourceName = normalizeOptionalString(result.response.source?.name);

  const schemaScopes = discoveryResult.categories.flatMap((category) =>
    category.schemas.flatMap((schema) => {
      const schemaScope = createOrganizationScope(schema.organizationId, schema.organizationName);
      if (!schemaScope) {
        return [];
      }

      const groupMatches = !groupId || schema.groupId === groupId;
      const schemaMatches =
        schema.id === schemaId ||
        sourceLabelMatches(schema.id, sourceId, sourceName) ||
        sourceLabelMatches(schema.name, sourceId, sourceName) ||
        sourceLabelMatches(schema.displayName, sourceId, sourceName);

      return groupMatches && schemaMatches ? [schemaScope] : [];
    }),
  );

  const templateScopes = (discoveryResult.templates ?? []).flatMap((template) => {
    const templateScope = createOrganizationScope(template.organizationId, template.organizationName);
    if (!templateScope) {
      return [];
    }

    const schemaMatches = !schemaId || template.schemaId === schemaId;
    const groupMatches = !groupId || template.groupId === groupId;
    return schemaMatches && groupMatches ? [templateScope] : [];
  });

  return getUniqueOrganizationScope([...schemaScopes, ...templateScopes]);
}

export function resolveGeneratedCreateSingleSourceOrganizationScope(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions = {},
): GeneratedSourceOrganizationScope | null {
  if (result.kind === 'composite') {
    return null;
  }

  const explicitScope = createOrganizationScope(options.sourceOrganizationId, options.sourceOrganizationName);
  if (explicitScope) {
    return explicitScope;
  }

  const sourceScope = getOrganizationScopeFromRecord(result.response.source);
  if (sourceScope) {
    return sourceScope;
  }

  const availableSourceScope = getUniqueOrganizationScope(
    (result.response.availableSources ?? []).map((source) => getOrganizationScopeFromRecord(source)),
  );
  if (availableSourceScope) {
    return availableSourceScope;
  }

  const matchedSourceScope = getUniqueOrganizationScope(
    (result.response.debug?.matchedSources ?? []).map((source) => getOrganizationScopeFromRecord(source)),
  );
  if (matchedSourceScope) {
    return matchedSourceScope;
  }

  return resolveDiscoveryOrganizationScope(result, options.discoveryResult);
}

function toPersistedTemplateQuery(query: TemplateQuery | undefined): PersistedTemplateQuery | null {
  if (!query) {
    return null;
  }

  const measures: PersistedTemplateQuery['measures'] = [];
  for (const measure of query.measures) {
    if (!isPersistedAggregation(measure.aggregation)) {
      return null;
    }

    measures.push({
      field: measure.field,
      aggregation: measure.aggregation,
      ...(measure.alias ? { alias: measure.alias } : {}),
    });
  }

  const groupBy = query.groupBy?.reduce<NonNullable<PersistedTemplateQuery['groupBy']>>((acc, entry) => {
    if (typeof entry === 'string') {
      acc.push(entry);
      return acc;
    }

    if (typeof entry.granularity === 'string') {
      acc.push({ field: entry.field, granularity: entry.granularity });
    }

    return acc;
  }, []);

  return {
    schemaId: query.schemaId,
    groupId: query.groupId,
    measures,
    ...(query.filters ? { filters: query.filters } : {}),
    ...(groupBy?.length ? { groupBy } : {}),
    ...(query.orderBy ? { orderBy: query.orderBy } : {}),
    ...(typeof query.limit === 'number' ? { limit: query.limit } : {}),
    ...(query.timeRange
      ? {
          timeRange: {
            ...(query.timeRange.field ? { field: query.timeRange.field } : {}),
            ...(query.timeRange.from ? { from: query.timeRange.from } : {}),
            ...(query.timeRange.to ? { to: query.timeRange.to } : {}),
            ...(query.timeRange.preset ? { preset: query.timeRange.preset } : {}),
          },
        }
      : {}),
  };
}

function toVariableDefinitions(
  variables: Record<string, TemplateVariable> | undefined,
): Record<string, VariableDefinition> {
  const normalized: Record<string, VariableDefinition> = {};

  for (const [key, variable] of Object.entries(variables ?? {})) {
    normalized[key] = {
      type: variable.type,
      ...(typeof variable.default === 'string' || typeof variable.default === 'number'
        ? { default: variable.default }
        : {}),
      ...(typeof variable.description === 'string' ? { description: variable.description } : {}),
      ...(Array.isArray(variable.options)
        ? {
            options: variable.options.filter((option): option is string => typeof option === 'string'),
          }
        : {}),
    };
  }

  return normalized;
}

function resolveTemplateTimeRangePreset(
  preset: string | undefined,
  templateVariables: Record<string, TemplateVariable> | undefined,
): string | undefined {
  const normalizedPreset = normalizeOptionalString(preset);
  if (!normalizedPreset) {
    return undefined;
  }

  if (!hasTemplatePlaceholder(normalizedPreset)) {
    return normalizedPreset;
  }

  const timeRangeDefault = templateVariables?.timeRange?.default;
  return isConcreteTemplateString(timeRangeDefault) ? timeRangeDefault.trim() : normalizedPreset;
}

function getSingleSourceFallbackApplyDisabledReason(
  result: Extract<CreateTemplateResult, { kind: 'template' }>,
): string | null {
  if (!result.response.templateQuery) {
    return MISSING_SINGLE_SOURCE_QUERY_REASON;
  }

  if (
    result.metadata.needsReview &&
    result.metadata.requestedIntentCategory !== undefined &&
    DIRECT_APPLY_BLOCKED_INTENTS.has(result.metadata.requestedIntentCategory)
  ) {
    return result.metadata.reviewReason ?? result.metadata.saveDisabledReason ?? DIRECT_APPLY_FALLBACK_REASON;
  }

  return null;
}

function getSharedGeneratedCreateDisabledReason(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions,
): string | null {
  if (result.kind === 'clarification' || result.metadata.needsClarification) {
    return (
      result.metadata.saveDisabledReason ??
      result.metadata.clarificationReason ??
      'This generated result needs clarification before it can be used.'
    );
  }

  if (!hasPotentialActionTarget(result)) {
    return result.metadata.saveDisabledReason ?? result.metadata.reviewReason ?? MISSING_SINGLE_SOURCE_QUERY_REASON;
  }

  const compositeSourceIdentityDisabledReason = getCompositeSourceIdentityDisabledReason(result);
  if (compositeSourceIdentityDisabledReason) {
    return result.metadata.saveDisabledReason ?? result.metadata.reviewReason ?? compositeSourceIdentityDisabledReason;
  }

  if (result.metadata.needsReview && result.metadata.reviewAcceptanceAllowed === false) {
    return result.metadata.saveDisabledReason ?? result.metadata.reviewReason ?? DIRECT_APPLY_FALLBACK_REASON;
  }

  if (result.metadata.needsReview && !options.reviewAccepted) {
    return result.metadata.saveDisabledReason ?? result.metadata.reviewReason ?? 'Accept the review before continuing.';
  }

  return null;
}

function getSaveSpecificDisabledReason(result: CreateTemplateResult): string | null {
  if (result.kind !== 'template') {
    return null;
  }

  const measures = result.response.templateQuery?.measures ?? [];
  return measures.every((measure) => isSupportedAggregation(measure.aggregation))
    ? null
    : UNSUPPORTED_AGGREGATION_REASON;
}

function getApplySpecificDisabledReason(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions,
): string | null {
  if (result.kind === 'composite') {
    return null;
  }

  if (result.kind === 'clarification') {
    return (
      result.metadata.saveDisabledReason ??
      result.metadata.clarificationReason ??
      'This generated result needs clarification before it can be used.'
    );
  }

  const fallbackDisabledReason = getSingleSourceFallbackApplyDisabledReason(result);
  if (fallbackDisabledReason) {
    return fallbackDisabledReason;
  }

  const sourceOrganizationScope = resolveGeneratedCreateSingleSourceOrganizationScope(result, options);
  const queryResolution = buildGeneratedCreateSingleSourceQueryDefinition({
    templateQuery: result.response.templateQuery,
    templateVariables: result.response.templateVariables,
    schemaName: result.response.source?.name,
    organizationId: sourceOrganizationScope?.organizationId,
    organizationName: sourceOrganizationScope?.organizationName,
  });

  if (!queryResolution.ok) {
    return queryResolution.disabledReason;
  }

  return options.isPersonalSpace && !sourceOrganizationScope
    ? PERSONAL_SPACE_SINGLE_SOURCE_SCOPE_ERROR
    : null;
}

export function isGeneratedCreateReviewAcceptanceRequired(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions = {},
): boolean {
  return (
    result.metadata.needsReview &&
    result.metadata.reviewAcceptanceAllowed !== false &&
    !options.reviewAccepted &&
    hasPotentialActionTarget(result)
  );
}

export function getGeneratedCreateDisabledReasons(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions = {},
): GeneratedCreateDisabledReasons {
  const sharedDisabledReason = getSharedGeneratedCreateDisabledReason(result, options);

  return {
    save: sharedDisabledReason ?? getSaveSpecificDisabledReason(result),
    apply: sharedDisabledReason ?? getApplySpecificDisabledReason(result, options),
  };
}

export function getGeneratedCreateActionState(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions = {},
): GeneratedCreateActionState {
  const disabledReasons = getGeneratedCreateDisabledReasons(result, options);
  const requiresReviewAcceptance = isGeneratedCreateReviewAcceptanceRequired(result, options);
  const canSave = disabledReasons.save === null;
  const canApply = disabledReasons.apply === null;

  return {
    ...disabledReasons,
    isActionable: canSave || canApply || requiresReviewAcceptance,
    canSave,
    canApply,
    requiresReviewAcceptance,
  };
}

export function isGeneratedCreateResultActionable(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions = {},
): boolean {
  return getGeneratedCreateActionState(result, options).isActionable;
}

export function buildGeneratedCreateSingleSourceQueryDefinition(
  input: GeneratedCreateSingleSourceQueryInput,
): GeneratedCreateSingleSourceQueryResult {
  if (!input.templateQuery) {
    return {
      ok: false,
      disabledReason: MISSING_SINGLE_SOURCE_QUERY_REASON,
    };
  }

  const measures: ChartQueryDefinition['measures'] = [];
  for (const measure of input.templateQuery.measures) {
    if (!isSupportedAggregation(measure.aggregation)) {
      return {
        ok: false,
        disabledReason: UNSUPPORTED_AGGREGATION_REASON,
      };
    }

    measures.push({
      field: measure.field,
      aggregation: measure.aggregation,
      ...(measure.alias ? { alias: measure.alias } : {}),
    });
  }

  const groupBy: NonNullable<ChartQueryDefinition['groupBy']> = [];
  for (const entry of input.templateQuery.groupBy ?? []) {
    if (typeof entry === 'string') {
      groupBy.push({ field: entry });
      continue;
    }

    if (entry.granularity !== undefined && !isTimeGranularity(entry.granularity)) {
      return {
        ok: false,
        disabledReason: UNSUPPORTED_GROUP_BY_GRANULARITY_REASON,
      };
    }

    groupBy.push({
      field: entry.field,
      ...(entry.granularity ? { granularity: entry.granularity } : {}),
    });
  }

  const resolvedPreset = resolveTemplateTimeRangePreset(
    input.templateQuery.timeRange?.preset,
    input.templateVariables,
  );

  const queryDefinition: ChartQueryDefinition = {
    schemaId: input.templateQuery.schemaId,
    groupId: input.templateQuery.groupId,
    schemaName: normalizeOptionalString(input.schemaName) ?? input.templateQuery.schemaId,
    measures,
    ...(input.organizationId ? { organizationId: input.organizationId } : {}),
    ...(input.organizationName ? { organizationName: input.organizationName } : {}),
    ...(input.templateQuery.timeRange
      ? {
          timeRange: {
            field: input.templateQuery.timeRange.field,
            ...(resolvedPreset ? { preset: resolvedPreset } : {}),
            ...(input.templateQuery.timeRange.from ? { from: input.templateQuery.timeRange.from } : {}),
            ...(input.templateQuery.timeRange.to ? { to: input.templateQuery.timeRange.to } : {}),
          },
        }
      : {}),
    ...(groupBy.length > 0 ? { groupBy } : {}),
    ...(input.templateQuery.filters ? { filters: input.templateQuery.filters } : {}),
    ...(input.templateQuery.orderBy ? { orderBy: input.templateQuery.orderBy } : {}),
    ...(typeof input.templateQuery.limit === 'number' ? { limit: input.templateQuery.limit } : {}),
  };

  if (hasUnresolvedTemplatePlaceholder(queryDefinition)) {
    return {
      ok: false,
      disabledReason: UNRESOLVED_TEMPLATE_VARIABLE_REASON,
    };
  }

  return {
    ok: true,
    queryDefinition,
  };
}

export function buildGeneratedCreateCompositeApplyPayload(
  result: Extract<CreateTemplateResult, { kind: 'composite' }>,
): {
  type: 'composite';
  selection: CompositeBindingSelection;
} {
  return {
    type: 'composite',
    selection: {
      definition: result.definition,
    },
  };
}

export function buildGeneratedCreateSavePayload(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions = {},
): GeneratedCreateSavePayloadResult {
  const disabledReasons = getGeneratedCreateDisabledReasons(result, options);
  if (disabledReasons.save) {
    return {
      ok: false,
      disabledReason: disabledReasons.save,
    };
  }

  if (result.kind === 'composite') {
    return {
      ok: true,
      payload: {
        templateType: 'composite-query',
        compositeQueryDefinition: result.definition,
      },
    };
  }

  const persistedQuery = toPersistedTemplateQuery(result.response.templateQuery);
  if (!persistedQuery) {
    return {
      ok: false,
      disabledReason: UNSUPPORTED_AGGREGATION_REASON,
    };
  }

  return {
    ok: true,
    payload: {
      query: persistedQuery,
      variables: toVariableDefinitions(result.response.templateVariables),
    },
  };
}

export function buildGeneratedCreateApplyPayload(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions = {},
): GeneratedCreateApplyPayloadResult {
  const disabledReasons = getGeneratedCreateDisabledReasons(result, options);
  if (disabledReasons.apply) {
    return {
      ok: false,
      disabledReason: disabledReasons.apply,
    };
  }

  if (result.kind === 'composite') {
    return {
      ok: true,
      payload: buildGeneratedCreateCompositeApplyPayload(result),
    };
  }

  const sourceOrganizationScope = resolveGeneratedCreateSingleSourceOrganizationScope(result, options);
  const queryResolution = buildGeneratedCreateSingleSourceQueryDefinition({
    templateQuery: result.response.templateQuery,
    templateVariables: result.response.templateVariables,
    schemaName: result.response.source?.name,
    organizationId: sourceOrganizationScope?.organizationId,
    organizationName: sourceOrganizationScope?.organizationName,
  });

  if (!queryResolution.ok) {
    return {
      ok: false,
      disabledReason: queryResolution.disabledReason,
    };
  }

  return {
    ok: true,
    payload: {
      type: 'single-source',
      queryDefinition: queryResolution.queryDefinition,
      ...(sourceOrganizationScope
        ? {
            organizationId: sourceOrganizationScope.organizationId,
            ...(sourceOrganizationScope.organizationName
              ? { organizationName: sourceOrganizationScope.organizationName }
              : {}),
          }
        : {}),
    },
  };
}

export type { CompositeBindingSelection, CompositeQueryDefinition };
